import { useEffect, useState } from 'react';

import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';

import { C, withBase } from '../MapSharedData';
import { ISO3_TO_ISO2 } from '../iso3ToIso2';

// Same de jure world boundaries used everywhere else (geoBoundaries CGAZ
// ADM0). ISO alpha-3 lives in `shapeGroup`.
const WORLD_GEOJSON_URL = '/geojson/world-countries.geojson';
const ISO3_PROP = 'shapeGroup';

const OUTLINE_SOURCE = 'country-outline';
const OUTLINE_LAYER = 'country-outline-line';

const ISO2_TO_ISO3: Record<string, string> = Object.fromEntries(
  Object.entries(ISO3_TO_ISO2).map(([iso3, iso2]) => [iso2, iso3]),
);

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

// Module-level cache mirrors the legacy useCountryOutline pattern so a single
// world-geojson fetch is shared across the lifecycle. Cleared only on full
// page reload, which is what we want — the dataset is stable per build.
let cachedWorldGeoJson: GeoJSON.FeatureCollection | null = null;
let pendingWorldFetch: Promise<GeoJSON.FeatureCollection> | null = null;

async function loadWorldGeoJson(): Promise<GeoJSON.FeatureCollection> {
  if (cachedWorldGeoJson) return cachedWorldGeoJson;
  if (pendingWorldFetch) return pendingWorldFetch;
  pendingWorldFetch = (async () => {
    const res = await fetch(withBase(WORLD_GEOJSON_URL));
    const geojson = (await res.json()) as GeoJSON.FeatureCollection;
    cachedWorldGeoJson = geojson;
    return geojson;
  })();
  return pendingWorldFetch;
}

interface UseUnifiedCountryOutlineOptions {
  mapRef: React.MutableRefObject<MapLibreMap | null>;
  ready: boolean;
  activeCountry: string | null;
}

/**
 * Step 2 of REFACTOR_PLAN.md: adds the country-outline source (empty) + line
 * layer (line-opacity 0) on the unified map. When activeCountry resolves to a
 * known feature in the world geojson, setData updates the source. The layer
 * stays invisible until step 3 fades line-opacity in on drill-in.
 */
export function useUnifiedCountryOutline({
  mapRef,
  ready,
  activeCountry,
}: UseUnifiedCountryOutlineOptions) {
  const [world, setWorld] = useState<GeoJSON.FeatureCollection | null>(cachedWorldGeoJson);

  // Lazy-load the world geojson (cached) so we can resolve country features.
  useEffect(() => {
    if (world) return;
    let cancelled = false;
    loadWorldGeoJson()
      .then((data) => {
        if (!cancelled) setWorld(data);
      })
      .catch((err) => {
        console.error('Failed to load world geojson for unified country outline', err);
      });
    return () => {
      cancelled = true;
    };
  }, [world]);

  // Add empty source + hidden line layer once. Sync after ready so subsequent
  // hook layers stack above this one (matches the plan's z-order: outline
  // above world layers, below region/postcode layers).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    if (map.getSource(OUTLINE_SOURCE)) return;

    map.addSource(OUTLINE_SOURCE, { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: OUTLINE_LAYER,
      type: 'line',
      source: OUTLINE_SOURCE,
      paint: {
        'line-color': C.primary,
        'line-width': 2.5,
        'line-opacity': 0,
        'line-blur': 0.5,
      },
    });
  }, [mapRef, ready]);

  // Pipe the matching feature in when activeCountry changes. Empty-FC reset
  // when the user returns to the world view (step 4 wires the visual side).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !world) return;
    const source = map.getSource(OUTLINE_SOURCE) as maplibregl.GeoJSONSource | undefined;
    if (!source) return;

    if (!activeCountry) {
      source.setData(EMPTY_FC);
      return;
    }
    const iso3 = ISO2_TO_ISO3[activeCountry];
    if (!iso3) {
      source.setData(EMPTY_FC);
      return;
    }
    const feature = world.features.find((f) => String(f.properties?.[ISO3_PROP] ?? '') === iso3);
    if (!feature) {
      source.setData(EMPTY_FC);
      return;
    }
    source.setData({ type: 'FeatureCollection', features: [feature] });
  }, [mapRef, ready, world, activeCountry]);
}
